import React, { useState, useEffect } from 'react';
import { Team, TeamMember } from '../../types';
import { AddMemberForm } from './AddMemberForm';
import { MemberList } from './MemberList';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Skeleton } from "@pagespeedify/ui";
import { Pencil, Save, XCircle, Loader2 } from 'lucide-react';
import { toast } from "sonner";

interface TeamManagementViewProps {
  team: Team;
  members: TeamMember[] | undefined;
  isLoadingMembers: boolean;
  isMembersError: boolean;
  membersError: Error | null;
  onAddMember: (email: string) => void;
  onRemoveMember: (memberId: string) => void;
  onRenameTeam: (data: { teamId: string; name: string }) => void;
  isRenaming: boolean;
  isAddingMember: boolean;
  isRemovingMemberId: string | null;
  refetchMembers: () => void;
}

const MAX_MEMBERS = 4;

export const TeamManagementView: React.FC<TeamManagementViewProps> = ({
  team,
  members,
  isLoadingMembers,
  isMembersError,
  membersError,
  onAddMember,
  onRemoveMember,
  onRenameTeam,
  isRenaming,
  isAddingMember,
  isRemovingMemberId,
  refetchMembers,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(team.name);

  useEffect(() => {
    setEditedName(team.name);
  }, [team.name]);

  const handleEditClick = () => {
    setEditedName(team.name);
    setIsEditingName(true);
  };

  const handleCancelClick = () => {
    setIsEditingName(false);
    setEditedName(team.name);
  };

  const handleSaveClick = () => {
    const trimmedName = editedName.trim();
    if (trimmedName && trimmedName !== team.name) {
      onRenameTeam({ teamId: team.id, name: trimmedName });
      setIsEditingName(false);
    } else if (!trimmedName) {
      toast.error("Team name cannot be empty.");
    } else {
      setIsEditingName(false);
    }
  };

  const currentMemberCount = members?.length ?? 0;
  const canAddMembers = !isLoadingMembers && currentMemberCount < MAX_MEMBERS;

  return (
    <div className="space-y-4">
      {/* Team Info Card */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-x-4">
          {isEditingName ? (
            <div className="flex-grow space-y-2">
              <Input
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                maxLength={12}
                className="text-lg font-semibold"
                disabled={isRenaming}
              />
              <div className="flex space-x-2">
                <Button onClick={handleSaveClick} disabled={isRenaming || !editedName.trim() || editedName.trim() === team.name}>
                  {isRenaming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  Save
                </Button>
                <Button variant="outline" onClick={handleCancelClick} disabled={isRenaming}>
                  <XCircle className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex-grow">
              <CardTitle className="flex items-center">
                Team: {team.name}
                <Button variant="ghost" size="icon" onClick={handleEditClick} className="ml-2 h-6 w-6" title="Rename Team">
                  <Pencil className="h-4 w-4" />
                </Button>
              </CardTitle>
              <CardDescription>Manage your team details and members.</CardDescription>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Add Member Card (Conditional on !isLoading and count) */}
      {canAddMembers && (
        <Card>
          <CardHeader>
            <CardTitle>Add New Member</CardTitle>
            <CardDescription>Enter the email address of the member you want to add.</CardDescription>
          </CardHeader>
          <CardContent>
            <AddMemberForm onSubmit={onAddMember} isAdding={isAddingMember} />
          </CardContent>
        </Card>
      )}

      {/* Member List Card */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({isLoadingMembers ? '...' : currentMemberCount})</CardTitle>
          {!isLoadingMembers && currentMemberCount >= MAX_MEMBERS && (
            <CardDescription className="text-orange-500">
              You have reached the maximum of {MAX_MEMBERS} members.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <MemberList
            members={members}
            isLoading={isLoadingMembers}
            isError={isMembersError}
            error={membersError}
            onRemove={onRemoveMember}
            isRemovingMemberId={isRemovingMemberId}
            refetchMembers={refetchMembers}
          />
        </CardContent>
      </Card>
    </div>
  );
};